import { desplazamiento } from '../lib/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, mensajeError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import FirmaDedo from '../components/FirmaDedo';
import { Loader2, CheckCircle2, Download, AlertCircle, Camera, Clock, RefreshCw } from 'lucide-react';
import ejemploFrontal from '../assets/ejemplo_foto_frontal.jpg';
import ejemploOblicua from '../assets/ejemplo_foto_oblicua.jpg';

// Las dos fotos del enlace de casa, con ejemplo (2026-08-21): van directas a
// la galería «antes» de la historia. La tablet NO pide fotos.
// Y desde el 2026-09-15 tampoco se piden cuando la CITA de este enlace es una
// revisión sucesiva o una intervención: a esos pacientes ya se les fotografió
// aquí. Lo contesta el servidor en `pide_fotos`, con la misma regla que el
// cuestionario de salud (salud_router.pide_fotos_para_cita).
const FOTOS_CASA = [
  { vista: 'frontal', titulo: '1 · De frente', ejemplo: ejemploFrontal,
    ayuda: 'Mirando a la cámara, cara centrada, buena luz.' },
  { vista: 'oblicua', titulo: '2 · Girada (45°)', ejemplo: ejemploOblicua,
    ayuda: 'Media vuelta hacia un lado, como en el ejemplo.' },
];
import { toast } from 'sonner';
import { useClinica, pieClinica } from '../lib/clinica';
import { textosAhPrevio } from '../lib/especialidad';
import {
  NO_LO_RECUERDA, esOrigenQueYaNoExiste, etiquetaParaElPaciente, useOpcionesComoNosConocio,
} from '../lib/origenes';
import {
  useTelefonoClinica, respuestaPerdida, cuandoReintentar, aQuienLlamar, fotoRechazada,
} from '../lib/enlacePaciente';
import LogoClinica from '../components/LogoClinica';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Longitudes máximas = las del backend (models.py LOPDPublicSubmit, Address y
// SaludRespuestas), para que un 422 por texto largo no llegue a producirse
// (revisión de enlaces 2026-09-07).
const MAX = {
  name: 100, surname1: 100, nif: 20, email: 120, phone: 30,
  calle: 200, poblacion: 100, codigo_postal: 10,
  salud_text: 1000, mejorar: 2000,
};

// «¿Cómo nos conoció?» (23/9/2026, decisión de dirección): obligatorio también
// al cerrar la LOPD, con las opciones del catálogo VIGENTE del KPI «Captación
// por origen» (lo edita dirección) y «No lo recuerda» para quien de verdad no
// lo sabe. Las pide la página a la puerta pública del catálogo
// (`useOpcionesComoNosConocio({ publico: true })`; mientras no contesta, solo
// «No lo recuerda», ver `lib/origenes.js`): lo que se guarda es el valor del
// catálogo, y al paciente se le enseña entero («Recomendación médica» y no
// «Rec. médica»). Un canal que no está en el catálogo no sale mientras
// dirección no lo añada: el KPI no lo cuenta aparte.
//
// LO QUE SE ENVÍA ES LO QUE SE ENSEÑÓ (segunda pasada del supervisor,
// 23/9/2026): el valor de cada opción es el texto que lee el paciente
// («Recomendación médica»), no la abreviatura del catálogo. El papel firmado
// lo guarda y lo imprime tal cual; la ficha se queda con «Rec. médica», que
// lo pone el servidor.

const COUNTRY_OPTIONS = [
  'España',
  'Portugal',
  'Francia',
  'Italia',
  'Reino Unido',
  'Alemania',
  'Otro',
];

// Phase 23.6 — Selector de fecha amigable para tablet.
// Phase 23.7 — Cambiado de Shadcn Select (Radix) a `<select>` HTML nativo:
// en Safari iPad, Radix Select tiene problemas conocidos con eventos táctiles
// dentro de portales (los SelectItem no responden al tap). El `<select>` nativo
// activa el wheel-picker rueda nativo del iOS, que es la UX ideal en tablet.
// Internamente sigue manejando un string ISO "YYYY-MM-DD" para no romper la
// integración con el backend.
const MONTHS_ES = [
  { v: '01', label: 'Enero' },
  { v: '02', label: 'Febrero' },
  { v: '03', label: 'Marzo' },
  { v: '04', label: 'Abril' },
  { v: '05', label: 'Mayo' },
  { v: '06', label: 'Junio' },
  { v: '07', label: 'Julio' },
  { v: '08', label: 'Agosto' },
  { v: '09', label: 'Septiembre' },
  { v: '10', label: 'Octubre' },
  { v: '11', label: 'Noviembre' },
  { v: '12', label: 'Diciembre' },
];
const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 111 }, (_, i) => CURRENT_YEAR - 5 - i);
const DAYS_31 = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

// Estilo replicando el look de un Input/SelectTrigger de Shadcn pero usando
// <select> nativo. h-12 garantiza objetivo táctil >= 44px (HIG iOS). Lo
// comparten la fecha de nacimiento, el país y «¿Cómo nos conociste?»
// (revisión de enlaces 2026-09-07: en Safari iPad el Select de Radix abría el
// menú pero al tocar una opción no pasaba nada, igual que pasó con la fecha).
function clsSelectNativo(hasError) {
  return [
    'flex w-full h-12 items-center justify-between rounded-md border bg-background',
    'px-3 py-2 text-base ring-offset-background',
    'focus:outline-none focus:ring-2 focus:ring-[#2E6E5E] focus:ring-offset-1',
    'appearance-none cursor-pointer',
    hasError ? 'border-red-500' : 'border-input',
  ].join(' ');
}

function BirthDatePicker({ value, onChange, onBlur, hasError }) {
  // Phase 23.7 fix — usamos estado LOCAL para cada parte (día/mes/año) porque
  // si derivamos del prop `value` (ISO completo) y sólo notificamos al padre
  // cuando los 3 están seleccionados, los selects intermedios "se borran" al
  // re-renderizar (el bug que reportó el usuario: el menú se abre pero al
  // tocar una opción no pasa nada, porque React lo resetea instantáneamente).
  // Inicializamos el estado local desde `value` para soportar edición posterior.
  const [parts, setParts] = useState(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      return { day: d, month: m, year: y };
    }
    return { day: '', month: '', year: '' };
  });

  // Si el padre cambia el value (p.ej. reset del form), sincronizamos.
  useEffect(() => {
    if (!value) {
      setParts({ day: '', month: '', year: '' });
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      setParts((prev) =>
        prev.day === d && prev.month === m && prev.year === y ? prev : { day: d, month: m, year: y }
      );
    }
  }, [value]);

  const handleChange = (field, val) => {
    const next = { ...parts, [field]: val };
    setParts(next);
    // Notificamos al padre sólo si los 3 están completos; si falta alguno,
    // limpiamos el value del padre para que la validación marque "obligatorio".
    if (next.day && next.month && next.year) {
      onChange(`${next.year}-${next.month}-${next.day}`);
    } else {
      onChange('');
    }
  };

  const selectCls = clsSelectNativo(hasError);

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        value={parts.day}
        onChange={(e) => handleChange('day', e.target.value)}
        onBlur={onBlur}
        className={selectCls}
        data-testid="public-lopd-birth-day"
        aria-label="Día"
      >
        <option value="" disabled>Día</option>
        {DAYS_31.map(d => (
          <option key={d} value={d}>{parseInt(d, 10)}</option>
        ))}
      </select>
      <select
        value={parts.month}
        onChange={(e) => handleChange('month', e.target.value)}
        onBlur={onBlur}
        className={selectCls}
        data-testid="public-lopd-birth-month"
        aria-label="Mes"
      >
        <option value="" disabled>Mes</option>
        {MONTHS_ES.map(m => (
          <option key={m.v} value={m.v}>{m.label}</option>
        ))}
      </select>
      <select
        value={parts.year}
        onChange={(e) => handleChange('year', e.target.value)}
        onBlur={onBlur}
        className={selectCls}
        data-testid="public-lopd-birth-year"
        aria-label="Año"
      >
        <option value="" disabled>Año</option>
        {BIRTH_YEARS.map(y => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
    </div>
  );
}

function Asterisk() {
  return <span className="text-red-600 ml-0.5">*</span>;
}

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" /> {msg}
    </p>
  );
}

/** Pantalla a página completa con un icono y un mensaje (estados del enlace de casa). */
function PantallaAviso({ icono: Icono, tono, titulo, texto, testid, children }) {
  const fondo = tono === 'ok' ? 'bg-green-100' : tono === 'espera' ? 'bg-amber-50' : 'bg-red-50';
  const color = tono === 'ok' ? 'text-green-600' : tono === 'espera' ? 'text-amber-600' : 'text-red-600';
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F6F2] to-white flex items-center justify-center p-6">
      <Card className="w-full max-w-xl bg-white shadow-lg" data-testid={testid}>
        <CardContent className="text-center pt-10 pb-8 px-8">
          <div className={`mx-auto w-14 h-14 ${fondo} rounded-full flex items-center justify-center mb-4`}>
            <Icono className={`w-8 h-8 ${color}`} />
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#13231E]">{titulo}</h1>
          {texto && <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{texto}</p>}
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hoja LOPD para tablet (PUBLIC kiosk).
 * Phase 17.2: una sola pantalla scrolleable con la firma abajo.
 * Validación estricta de campos obligatorios — sin "Continuar/Atrás".
 */
export default function PublicLopdPage() {
  const clinicaDatos = useClinica();
  // Modo "casa": el paciente abre el enlace desde su móvil (no es el kiosco de
  // la clínica). Cambia el texto y quita el botón "siguiente paciente".
  const esCasa = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('casa') === '1';
  const casaToken = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('t') || '')
    : '';
  // La cita para la que se mandó este enlace. Viaja en la URL y no dentro del
  // enlace (2026-09-15): el enlace es uno por paciente y se reutiliza
  // mientras siga sin rellenar, así que lo que se guardase en él se lo comía
  // la siguiente cita que pasara por delante. Es lo único que decide si se le
  // piden las dos fotos; sin ella se piden, que es el lado seguro.
  const casaCita = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('cita') || '')
    : '';
  const urlEnlace = `/api/public/lopd/casa/${encodeURIComponent(casaToken)}`
    + (casaCita ? `?cita=${encodeURIComponent(casaCita)}` : '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const sigRef = useRef(null);
  // Estado inicial extraído como factory para poder resetear tras envío
  // (Phase 23.6 fix: el botón "Listo" debe limpiar y volver al formulario
  // en blanco, no navegar a otra ruta).
  const buildInitialForm = () => ({
    name: '',
    surname1: '',
    nif: '',
    birth_date: '',
    email: '',
    phone: '',
    address: { calle: '', poblacion: '', codigo_postal: '', pais: 'España' },
    how_known_us: '',
    consent_reminders: false,
    consent_commercial: false,
  });
  const [form, setForm] = useState(buildInitialForm);
  // Las opciones de «¿Cómo nos conoció?», del catálogo vigente (23/9/2026).
  // `versionOrigenes` sube si el servidor dice que la elegida ya no existe.
  const [versionOrigenes, setVersionOrigenes] = useState(0);
  const opcionesOrigen = useOpcionesComoNosConocio({ publico: true, version: versionOrigenes });
  // Cuestionario de salud (documento único LOPD+salud, 2026-07-26).
  const buildInitialSalud = () => ({
    alergias: null, alergias_text: '',
    medicacion: null, medicacion_text: '',
    hialuronico: null, hialuronico_text: '',
    cirugias: null, cirugias_text: '',
  });
  const [salud, setSalud] = useState(buildInitialSalud);
  const setS = (k, v) => setSalud((p) => ({ ...p, [k]: v }));
  // vista → { subiendo, url (vista previa local), ok } — solo enlace de casa
  const [fotos, setFotos] = useState({ frontal: null, oblicua: null });
  // ¿Le pedimos las fotos? Lo contesta el servidor con el tipo de la cita que
  // va en la URL. Se empieza en `true` y solo un `pide_fotos: false` explícito
  // las quita: si el servidor es de antes del cambio y no manda el campo, el
  // paciente ve lo de siempre en vez de quedarse sin poder subirlas.
  const [pideFotos, setPideFotos] = useState(true);
  // Error de la última foto, pintado dentro del recuadro «Tus dos fotos»
  // además del toast (revisión de enlaces 2026-09-07).
  const [fotoError, setFotoError] = useState('');

  // Estado del enlace de casa, comprobado ANTES de enseñar el formulario
  // (revisión de enlaces 2026-09-07): con un token caducado, ya usado o
  // inválido el paciente rellenaba todo (datos, cuestionario, dos fotos y
  // firma) y se enteraba al enviar; y tras enviar con éxito, al reabrir el
  // enlace veía el formulario en blanco. Estados: cargando | pendiente |
  // completado | caducado | invalido | incompleto (422: se cortó al copiarlo,
  // 23/9/2026) | espera (429) | error (no se pudo comprobar).
  const [enlace, setEnlace] = useState(() => ({ estado: esCasa ? 'cargando' : 'pendiente', nombre: '' }));
  const [intentoEnlace, setIntentoEnlace] = useState(0);
  // El 429 al abrir el enlace, para decir cuánto esperar (23/9/2026).
  const [falloEnlace, setFalloEnlace] = useState(null);
  // Por qué no se ha guardado el último envío, EN PANTALLA junto al botón
  // (23/9/2026). El 409 «El documento no coincide con la ficha…» era un
  // toast de 4 segundos: la paciente no sabía qué había pasado ni qué hacer,
  // y no podía terminar. {titulo, motivo, ayuda}; se va al volver a enviar.
  const [aviso, setAviso] = useState(null);

  useEffect(() => {
    if (!esCasa || !casaToken) return undefined;
    let vivo = true;
    setEnlace({ estado: 'cargando', nombre: '' });
    api.get(urlEnlace)
      .then((r) => {
        if (!vivo) return;
        const estado = r.data?.estado;
        setPideFotos(r.data?.pide_fotos !== false);
        setEnlace({
          estado: ['pendiente', 'completado', 'caducado'].includes(estado) ? estado : 'invalido',
          nombre: r.data?.nombre || '',
        });
      })
      .catch((e) => {
        if (!vivo) return;
        // 404 = el token no existe. Cualquier otra cosa (red caída, 5xx) no
        // dice nada del enlace: se ofrece reintentar.
        const status = e?.response?.status;
        // 422 = token más corto de lo que exige el servidor: se cortó al
        // copiarlo. No es un fallo de red (verificación 2026-09-07), pero
        // tampoco un enlace «no válido»: es un enlace INCOMPLETO, y se dice
        // así, como en el cuestionario de salud y en confirmar cita
        // (23/9/2026). Reintentar no lo completa: que abra el del mensaje.
        // 429 = el freno de peticiones: ni enlace malo ni avería, esperar y
        // saber cuánto (23/9/2026).
        setFalloEnlace(e);
        let estado = 'error';
        if (status === 404 || status === 410) estado = 'invalido';
        else if (status === 422) estado = 'incompleto';
        else if (status === 429) estado = 'espera';
        setEnlace({ estado, nombre: '' });
      });
    return () => { vivo = false; };
  }, [esCasa, casaToken, urlEnlace, intentoEnlace]);

  const subirFoto = async (vista, archivo) => {
    if (!archivo) return;
    const url = URL.createObjectURL(archivo);
    setFotoError('');
    setFotos((prev) => ({ ...prev, [vista]: { subiendo: true, url, ok: false } }));
    try {
      const fd = new FormData();
      fd.append('file', archivo);
      fd.append('vista', vista);
      await api.post(`/api/public/lopd/casa/${encodeURIComponent(casaToken)}/foto`, fd);
      setFotos((prev) => ({ ...prev, [vista]: { subiendo: false, url, ok: true } }));
    } catch (e) {
      setFotos((prev) => ({ ...prev, [vista]: null }));
      // 429: cuándo volver a intentarlo. Sin respuesta o 5xx: no se sabe si
      // llegó; volver a subirla es seguro, el servidor sustituye la anterior
      // (23/9/2026, igual que en el cuestionario de salud).
      let msg;
      if (e?.response?.status === 429) {
        msg = `Hay mucho movimiento desde tu conexión. Vuelve a intentarlo ${cuandoReintentar(e)}.`;
      } else if (e?.response?.status === 413 || e?.response?.status === 503) {
        // Foto demasiado grande (24/9/2026) o el servidor ocupado con otras:
        // lo que dice el servidor, que va escrito para el paciente; y si el
        // 413 lo corta antes el proxy (sin texto), uno de tú.
        msg = fotoRechazada(e);
      } else if (respuestaPerdida(e)) {
        msg = 'No hemos podido confirmar que la foto se haya subido. Comprueba tu conexión y '
          + 'vuelve a subirla (si ya había llegado, se sustituye).';
      } else {
        msg = mensajeError(e, 'No se pudo subir la foto; inténtalo de nuevo.');
      }
      setFotoError(msg);
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setResult(null);
    setForm(buildInitialForm());
    setSalud(buildInitialSalud());
    setTouched({});
    setSubmitAttempted(false);
    sigRef.current?.clear();
    window.scrollTo({ top: 0, behavior: desplazamiento() });
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setA = (k, v) => setForm(f => ({ ...f, address: { ...f.address, [k]: v } }));
  const touch = (k) => setTouched(t => ({ ...t, [k]: true }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = 'Obligatorio';
    if (!form.surname1.trim()) e.surname1 = 'Obligatorio';
    if (!form.nif.trim()) e.nif = 'Obligatorio';
    if (!form.birth_date) e.birth_date = 'Obligatorio';
    if (!form.address.calle.trim()) e.calle = 'Obligatorio';
    if (!form.address.codigo_postal.trim()) e.codigo_postal = 'Obligatorio';
    if (!form.address.poblacion.trim()) e.poblacion = 'Obligatorio';
    if (!form.address.pais.trim()) e.pais = 'Obligatorio';
    if (!form.phone.trim()) e.phone = 'Obligatorio';
    // Lo rellena el propio paciente: se le dice qué hacer si no se acuerda.
    if (!form.how_known_us.trim()) e.how_known_us = `Elija una opción. Si no lo recuerda, elija «${NO_LO_RECUERDA}».`;
    if (!form.consent_reminders) e.consent_reminders = 'Debes aceptarlo para continuar';
    return e;
  }, [form]);

  const showErr = (k) => (submitAttempted || touched[k]) ? errors[k] : null;

  const computeAge = (iso) => {
    if (!iso) return '';
    try {
      const b = new Date(iso);
      if (Number.isNaN(b.getTime())) return '';
      const today = new Date();
      let age = today.getFullYear() - b.getFullYear();
      const m = today.getMonth() - b.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
      return age >= 0 && age < 130 ? String(age) : '';
    } catch { return ''; }
  };
  const age = computeAge(form.birth_date);

  const sigEmpty = () => !sigRef.current || sigRef.current.isEmpty();

  const submit = async () => {
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) {
      toast.error('Faltan campos obligatorios marcados con *');
      const firstField = document.querySelector('[data-field-error="true"]');
      if (firstField) firstField.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    // Cuestionario de salud: sí/no obligatorio y detalle obligatorio con sí.
    const saludBloques = [
      ['alergias', 'alergias o intolerancias a medicamentos'],
      ['medicacion', 'medicación habitual'],
      ['hialuronico', textosAhPrevio(clinicaDatos.clinic_profile).corto],
      ['cirugias', 'cirugías previas'],
    ];
    for (const [campo, label] of saludBloques) {
      const irSalud = () => document.getElementById('salud-box')
        ?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      if (salud[campo] === null) {
        toast.error('Cuestionario de salud: responde sí o no a todas las preguntas');
        irSalud();
        return;
      }
      if (salud[campo] === true && !(salud[`${campo}_text`] || '').trim()) {
        toast.error(`Cuestionario de salud: falta el detalle de ${label}`);
        irSalud();
        return;
      }
    }
    // Faltar fotos solo frena a quien se las hemos pedido: en una revisión
    // sucesiva o una intervención no se piden, y antes el envío se quedaba
    // bloqueado ahí para siempre (2026-09-15).
    if (esCasa && pideFotos && (!fotos.frontal?.ok || !fotos.oblicua?.ok)) {
      toast.error('Faltan tus dos fotos (de frente y girada 45°).');
      document.getElementById('fotos-box')?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    if (esCasa && !(salud.mejorar || '').trim()) {
      toast.error('Cuéntanos brevemente qué te gustaría mejorar o tratar.');
      document.getElementById('fotos-box')?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    if (sigEmpty()) {
      toast.error('Por favor, firma antes de enviar');
      const sigBox = document.getElementById('signature-box');
      if (sigBox) sigBox.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
      return;
    }
    setBusy(true);
    setAviso(null);
    try {
      const signature_b64 = sigRef.current.getCanvas().toDataURL('image/png');
      const payload = {
        ...form,
        nif: form.nif.trim().toUpperCase(),
        // El backend ignora el campo `surname2` (no obligatorio en este flujo)
        surname2: '',
        // Para mantener compatibilidad con el endpoint actual mapeamos
        // `consent_reminders` también a `consent_health_info` (es el mismo
        // consentimiento desde el punto de vista del paciente).
        consent_health_info: form.consent_reminders,
        signature_b64,
        // Documento único: el cuestionario de salud viaja con la LOPD y se
        // vuelca a la ficha con la misma firma.
        salud: { ...salud },
        // El backend necesita saber si esto viene del móvil del paciente para
        // no darle por llegado a la clínica (luz verde).
        casa: esCasa,
        public_token: esCasa ? casaToken : undefined,
      };
      const res = await api.post('/api/public/lopd/submit', payload);
      setResult(res.data);
    } catch (e) {
      // 410 en modo casa: el backend distingue «ya fue utilizado» (un envío
      // hecho o en curso) de «ha caducado».
      const detail = String(e?.response?.data?.detail || '');
      if (esCasa && e?.response?.status === 410 && /utilizad/i.test(detail)) {
        /* NO SE DEDUCE DEL ERROR QUE SU FORMULARIO HAYA LLEGADO
         * (2026-09-21). Aquí se daba por hecho que un 410 «ya fue utilizado»
         * significaba que el envío anterior SÍ se guardó, y se le enseñaba
         * «Formulario recibido». Con una reserva colgada —un envío que murió
         * a medias y dejó el enlace en «procesando»— eso era falso: la
         * paciente rellenaba diez minutos, firmaba, y se le decía que su
         * consentimiento había llegado cuando no había llegado nada, ni la
         * LOPD, ni el cuestionario con sus alergias, ni las fotos. Se iba
         * tranquila y no volvía a intentarlo.
         *
         * El agujero grande está tapado en el servidor (la reserva caduca a
         * los diez minutos, `documents_router._reserva_vencida`); esto es la
         * otra mitad y vale para lo que quede: dos envíos a la vez en los que
         * el que ganó la carrera revienta después. Antes de decirle a nadie
         * que su formulario llegó, SE PREGUNTA. Si el servidor no lo confirma
         * —dice que sigue pendiente, o no se le puede preguntar— se le dice
         * que no ha salido y se le deja reintentar con todo lo que escribió
         * delante, que sigue en pantalla. */
        let confirmado = false;
        try {
          const r = await api.get(urlEnlace);
          confirmado = r?.data?.estado === 'completado';
          if (r?.data?.estado === 'caducado') {
            setEnlace((prev) => ({ ...prev, estado: 'caducado' }));
            return;
          }
        } catch {
          // Tampoco se puede preguntar (23/9/2026): entonces no se sabe si
          // llegó, y «no hemos podido guardar» sería otro dato inventado.
          avisoNoSeSabe();
          return;
        }
        if (confirmado) {
          setEnlace((prev) => ({ ...prev, estado: 'completado' }));
          return;
        }
        toast.error('No hemos podido guardar tu formulario. No cierres esta '
          + 'página y vuelve a pulsar «Firmar y enviar».');
        return;
      }
      if (esCasa && e?.response?.status === 410 && /caducad/i.test(detail)) {
        setEnlace((prev) => ({ ...prev, estado: 'caducado' }));
        return;
      }
      // Rechazado porque lo escrito no casa con su ficha (409 del servidor:
      // «El documento/nombre no coincide con la ficha…»). Se queda en
      // pantalla con el motivo, qué revisar y a quién llamar (23/9/2026). El
      // teléfono cuenta: si coincide con el de su ficha, el servidor la da
      // por identificada aunque el DNI o el nombre estén escritos de otra
      // forma (documents_router._datos_contradicen_ficha).
      if (esCasa && e?.response?.status === 409) {
        mostrarAviso({
          titulo: 'No hemos podido guardar su formulario',
          motivo: detail ? `${detail.replace(/\.$/, '')}.` : '',
          ayuda: 'Revise que el DNI/NIE, el nombre y el teléfono sean los mismos que dio en la '
            + 'clínica y vuelva a pulsar «Firmar y enviar». Si están bien escritos, '
            + `${llamenosClinica} y lo solucionamos.`,
        });
        return;
      }
      // Lo que eligió en «¿Cómo nos conoció?» ya no está en la lista: la
      // clínica la cambió con la página abierta (el iPad no recarga entre
      // paciente y paciente). Se vuelve a pedir la lista, se deja el campo
      // por elegir y el porqué se queda en pantalla, no en un aviso que se
      // va a los cuatro segundos (23/9/2026).
      if (esOrigenQueYaNoExiste(e)) {
        setVersionOrigenes((v) => v + 1);
        setF('how_known_us', '');
        touch('how_known_us');
        mostrarAviso({
          titulo: 'Vuelva a elegir cómo nos conoció',
          motivo: detail ? `${detail.replace(/\.$/, '')}.` : '',
          ayuda: 'La lista de opciones se ha actualizado. Elija una de nuevo (si no lo '
            + `recuerda, «${NO_LO_RECUERDA}») y vuelva a pulsar «Firmar y enviar».`,
        });
        return;
      }
      if (e?.response?.status === 429) {
        mostrarAviso({
          titulo: 'Hay mucho movimiento en este momento',
          motivo: '',
          ayuda: `Vuelva a pulsar «Firmar y enviar» ${cuandoReintentar(e)}. Si no puede esperar, `
            + `${llamenosClinica}.`,
        });
        return;
      }
      // Sin respuesta o 5xx: pudo guardarse. Antes de decir nada se le
      // pregunta al servidor (23/9/2026); ver `comprobarEnvio`.
      if (esCasa && respuestaPerdida(e)) {
        await comprobarEnvio();
        return;
      }
      toast.error(mensajeError(e, 'No se pudo enviar. Inténtelo de nuevo'));
    } finally {
      setBusy(false);
    }
  };

  // El de Configuración, con el formato de los mensajes; si no se ha podido
  // leer (sin conexión tampoco llega), el último que este dispositivo leyó
  // bien de esta clínica; y si no hay ninguno, ninguno (23/9/2026): aquí
  // había un número fijo, el de una instalación, y este build sirve a otras clínicas.
  // Se vuelve a pedir tras cada comprobación del enlace (el `pedir` pasa a
  // falso mientras carga): así «Reintentar» también recupera el teléfono si
  // la Configuración falló la primera vez (23/9/2026, lib/clinica).
  const telefonoClinica = useTelefonoClinica(clinicaDatos.phone, enlace.estado !== 'cargando');
  const llamanosClinica = aQuienLlamar(telefonoClinica);
  const llamenosClinica = aQuienLlamar(telefonoClinica, { usted: true });

  /** Pone el aviso junto al botón de enviar y lo lleva a la vista. */
  function mostrarAviso(datos) {
    setAviso(datos);
    setTimeout(() => {
      document.getElementById('aviso-envio')
        ?.scrollIntoView({ behavior: desplazamiento(), block: 'center' });
    }, 0);
  }

  /**
   * El envío de casa se quedó sin contestación (o con un 5xx del proxy):
   * pudo guardarse. Se pregunta por el enlace: completado → «recibido»;
   * caducado → lo que es; pendiente → que no ha llegado, para volver a
   * enviarlo; y si ni eso se puede preguntar, que no se sabe. Nunca un
   * «no se pudo enviar» que no se sabe (23/9/2026).
   */
  async function comprobarEnvio() {
    let estado = null;
    try {
      const r = await api.get(urlEnlace);
      estado = r?.data?.estado || null;
    } catch {
      estado = null;
    }
    if (estado === 'completado' || estado === 'caducado') {
      setEnlace((prev) => ({ ...prev, estado }));
      return;
    }
    if (estado === 'pendiente') {
      mostrarAviso({
        titulo: 'Su formulario no ha llegado a la clínica',
        motivo: '',
        ayuda: 'Compruebe su conexión y vuelva a pulsar «Firmar y enviar»: lo que ha escrito '
          + `sigue aquí. Si sigue sin funcionar, ${llamenosClinica}.`,
      });
      return;
    }
    avisoNoSeSabe();
  }

  /** Ni el envío ni la pregunta han tenido contestación: no se sabe si llegó.
   *  Reenviar es seguro: si ya había llegado, el servidor contesta «ya
   *  utilizado» y se comprueba de nuevo. */
  function avisoNoSeSabe() {
    mostrarAviso({
      titulo: 'No hemos podido confirmar si su formulario ha llegado',
      motivo: '',
      ayuda: 'Compruebe su conexión y vuelva a pulsar «Firmar y enviar»: si ya había llegado, '
        + `se lo diremos. Si sigue sin funcionar, ${llamenosClinica}.`,
    });
  }

  // Sin token (`?casa=1` sin `t`) o con uno más corto de lo que exige el
  // servidor (422): el enlace se cortó al copiarlo. Es INCOMPLETO, no «no
  // válido», como en el cuestionario de salud y en confirmar cita (23/9/2026).
  if (esCasa && (!casaToken || enlace.estado === 'incompleto')) {
    return (
      <PantallaAviso icono={AlertCircle} tono="error" titulo="Este enlace está incompleto" testid="lopd-casa-incompleto"
        texto={`Ábrelo desde el mensaje completo que te enviamos. Si no lo encuentras, ${llamanosClinica} y te lo enviamos de nuevo.`} />
    );
  }
  if (esCasa && enlace.estado === 'cargando') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F0F6F2] to-white flex items-center justify-center p-6 text-muted-foreground" data-testid="lopd-casa-cargando">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Comprobando su enlace…
      </div>
    );
  }
  if (esCasa && enlace.estado === 'completado') {
    return (
      <PantallaAviso icono={CheckCircle2} tono="ok" titulo="Formulario recibido" testid="lopd-casa-recibido"
        texto="Ya hemos recibido su formulario; no hace falta volver a enviarlo." />
    );
  }
  if (esCasa && enlace.estado === 'caducado') {
    return (
      <PantallaAviso icono={Clock} tono="espera" titulo="Enlace caducado" testid="lopd-casa-caducado"
        texto={telefonoClinica
          ? `Este enlace ha caducado: pida uno nuevo a la clínica (${telefonoClinica}).`
          : 'Este enlace ha caducado: pida uno nuevo a la clínica.'} />
    );
  }
  if (esCasa && enlace.estado === 'invalido') {
    return (
      <PantallaAviso icono={AlertCircle} tono="error" titulo="Este enlace no es válido" testid="lopd-casa-invalido"
        texto={`Pide a la clínica que te envíe de nuevo el enlace seguro del formulario: ${llamanosClinica}.`} />
    );
  }
  // Sin conexión o avería (error) y el freno de peticiones (espera): el
  // enlace sigue siendo suyo; reintentar y, si no, llamar (23/9/2026).
  if (esCasa && (enlace.estado === 'error' || enlace.estado === 'espera')) {
    const espera = enlace.estado === 'espera';
    return (
      <PantallaAviso icono={espera ? Clock : AlertCircle} tono="espera"
        titulo={espera ? 'Hay mucho movimiento en este momento' : 'No hemos podido comprobar su enlace'}
        testid={espera ? 'lopd-casa-espera' : 'lopd-casa-error'}
        texto={espera
          ? `Vuelva a intentarlo ${cuandoReintentar(falloEnlace)}. Su enlace sigue siendo válido.`
          : 'Compruebe su conexión a internet e inténtelo de nuevo.'}>
        <Button variant="outline" className="mt-5" onClick={() => setIntentoEnlace((n) => n + 1)} data-testid="lopd-casa-reintentar">
          <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
        </Button>
        <p className="text-sm text-muted-foreground mt-4">
          Si sigue sin funcionar, {llamenosClinica}.
        </p>
      </PantallaAviso>
    );
  }

  // ----- Vista de confirmación tras envío -----
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2E6E5E] to-[#2E6E5E] flex items-center justify-center p-6">
        <Card className="w-full max-w-xl bg-white shadow-xl">
          <CardContent className="text-center pt-10 pb-8 px-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-[#13231E]">¡Gracias, {result.patient_name}!</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              {esCasa
                ? 'Hemos recibido tus datos y tu firma correctamente. Ya puedes cerrar esta página — no hace falta nada más.'
                : 'Tu consentimiento ha quedado registrado correctamente.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  const downloadToken = result.pdf_token || (esCasa ? casaToken : '');
                  const qs = downloadToken ? `?token=${encodeURIComponent(downloadToken)}` : '';
                  window.open(`${BACKEND_URL}/api/public/lopd/${result.lopd_id}/pdf${qs}`, '_blank');
                }}
              >
                <Download className="w-4 h-4 mr-2" /> Descargar mi copia
              </Button>
              {!esCasa && (
                <Button
                  onClick={resetForm}
                  className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                  data-testid="public-lopd-done-btn"
                >
                  Listo — siguiente paciente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F6F2] to-white">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoClinica className="h-9 w-9 rounded object-contain" data-testid="lopd-logo" />
            <div>
              <div className="font-display font-semibold text-[#13231E] leading-tight">{clinicaDatos.clinic_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Consentimiento LOPD</div>
            </div>
          </div>
          <Link to="/" className="text-xs text-muted-foreground hover:text-[#13231E] hidden sm:block">Acceso interno</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        <Card className="shadow-md">
          <CardContent className="pt-6 pb-8 sm:px-8 px-4 space-y-7">
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[#13231E]" data-testid="public-lopd-titulo">
                {esCasa && enlace.nombre ? `Hola, ${enlace.nombre}` : 'Bienvenido/a a la clínica'}
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Rellena los datos y firma al final para autorizar el tratamiento de tus datos personales.
                Los campos marcados con <span className="text-red-600 font-semibold">*</span> son obligatorios.
              </p>
            </div>

            {/* DATOS PERSONALES */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Datos personales</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div data-field-error={!!showErr('name')}>
                  <Label>Nombre<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.name} value={form.name} onChange={(e) => setF('name', e.target.value)} onBlur={() => touch('name')} data-testid="public-lopd-name" />
                  <FieldError msg={showErr('name')} />
                </div>
                <div data-field-error={!!showErr('surname1')}>
                  <Label>Apellido(s)<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.surname1} value={form.surname1} onChange={(e) => setF('surname1', e.target.value)} onBlur={() => touch('surname1')} placeholder="Apellido o ambos apellidos" data-testid="public-lopd-surname1" />
                  <FieldError msg={showErr('surname1')} />
                </div>
                <div data-field-error={!!showErr('nif')}>
                  <Label>DNI / NIE / Pasaporte<Asterisk /></Label>
                  <Input className="text-base h-11 uppercase" maxLength={MAX.nif} value={form.nif} onChange={(e) => setF('nif', e.target.value.toUpperCase())} onBlur={() => touch('nif')} data-testid="public-lopd-nif" />
                  <FieldError msg={showErr('nif')} />
                </div>
                <div data-field-error={!!showErr('birth_date')}>
                  <Label>Fecha de nacimiento{age && <span className="text-[#2E6E5E] ml-1 text-xs">({age} años)</span>}<Asterisk /></Label>
                  <BirthDatePicker
                    value={form.birth_date}
                    onChange={(v) => setF('birth_date', v)}
                    onBlur={() => touch('birth_date')}
                    hasError={!!showErr('birth_date')}
                  />
                  <FieldError msg={showErr('birth_date')} />
                </div>
                <div data-field-error={!!showErr('phone')}>
                  <Label>Teléfono<Asterisk /></Label>
                  <Input type="tel" inputMode="tel" className="text-base h-11" maxLength={MAX.phone} value={form.phone} onChange={(e) => setF('phone', e.target.value)} onBlur={() => touch('phone')} data-testid="public-lopd-phone" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Escriba el <strong>mismo teléfono</strong> con el que contactó con la
                    clínica o se gestionó su cita: nos sirve para localizar su ficha.
                  </p>
                  <FieldError msg={showErr('phone')} />
                </div>
                <div>
                  <Label>Email <span className="text-xs text-muted-foreground">(opcional)</span></Label>
                  <Input type="email" inputMode="email" className="text-base h-11" maxLength={MAX.email} value={form.email} onChange={(e) => setF('email', e.target.value)} data-testid="public-lopd-email" />
                </div>
              </div>
            </section>

            {/* DIRECCIÓN */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Dirección</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3" data-field-error={!!showErr('calle')}>
                  <Label>Calle y número<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.calle} value={form.address.calle} onChange={(e) => setA('calle', e.target.value)} onBlur={() => touch('calle')} data-testid="public-lopd-calle" />
                  <FieldError msg={showErr('calle')} />
                </div>
                <div data-field-error={!!showErr('codigo_postal')}>
                  <Label>Código Postal<Asterisk /></Label>
                  <Input className="text-base h-11" inputMode="numeric" maxLength={MAX.codigo_postal} value={form.address.codigo_postal} onChange={(e) => setA('codigo_postal', e.target.value)} onBlur={() => touch('codigo_postal')} data-testid="public-lopd-cp" />
                  <FieldError msg={showErr('codigo_postal')} />
                </div>
                <div data-field-error={!!showErr('poblacion')}>
                  <Label>Ciudad<Asterisk /></Label>
                  <Input className="text-base h-11" maxLength={MAX.poblacion} value={form.address.poblacion} onChange={(e) => setA('poblacion', e.target.value)} onBlur={() => touch('poblacion')} data-testid="public-lopd-poblacion" />
                  <FieldError msg={showErr('poblacion')} />
                </div>
                <div data-field-error={!!showErr('pais')}>
                  <Label>País<Asterisk /></Label>
                  <select
                    value={form.address.pais}
                    onChange={(e) => { setA('pais', e.target.value); touch('pais'); }}
                    onBlur={() => touch('pais')}
                    className={clsSelectNativo(!!showErr('pais'))}
                    aria-label="País"
                    data-testid="public-lopd-pais"
                  >
                    <option value="" disabled>País</option>
                    {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <FieldError msg={showErr('pais')} />
                </div>
              </div>
            </section>

            {/* CÓMO NOS CONOCIÓ */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">¿Cómo nos conoció?</h2>
              <div data-field-error={!!showErr('how_known_us')}>
                <Label>Elija una opción<Asterisk /></Label>
                <p className="text-xs text-muted-foreground mb-1">Si no lo recuerda, elija «{NO_LO_RECUERDA}».</p>
                <select
                  value={form.how_known_us}
                  onChange={(e) => { setF('how_known_us', e.target.value); touch('how_known_us'); }}
                  onBlur={() => touch('how_known_us')}
                  className={clsSelectNativo(!!showErr('how_known_us'))}
                  aria-label="¿Cómo nos conoció?"
                  aria-required="true"
                  data-testid="public-lopd-how-known"
                >
                  <option value="" disabled>Elija…</option>
                  {opcionesOrigen.map((o) => {
                    const texto = etiquetaParaElPaciente(o);
                    return <option key={o} value={texto}>{texto}</option>;
                  })}
                </select>
                <FieldError msg={showErr('how_known_us')} />
              </div>
            </section>

            {/* CONSENTIMIENTOS */}
            <section>
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Consentimientos</h2>
              <p className="text-xs text-muted-foreground mb-3">
                Responsable del tratamiento: <strong>{clinicaDatos.legal_entity || clinicaDatos.clinic_name}</strong>{clinicaDatos.address ? `, ${clinicaDatos.address}` : ''}.
                Finalidad: prestación de servicios médicos y comunicación con el paciente.
                Base jurídica: ejecución de contrato y consentimiento expreso.
                Derechos: acceso, rectificación, supresión, oposición, portabilidad y limitación en
                <a className="underline ml-1" href={`mailto:${clinicaDatos.email}`}>{clinicaDatos.email}</a>.
              </p>
              <div className={`flex items-start gap-3 p-3 rounded-md border ${showErr('consent_reminders') ? 'border-red-400 bg-red-50/30' : 'border-border bg-[#F0F6F2]/50'}`} data-field-error={!!showErr('consent_reminders')}>
                <Checkbox
                  checked={form.consent_reminders}
                  onCheckedChange={(v) => setF('consent_reminders', !!v)}
                  className="mt-1"
                  data-testid="public-lopd-consent-reminders"
                />
                <div className="flex-1">
                  <Label className="text-sm font-medium leading-snug cursor-pointer" onClick={() => setF('consent_reminders', !form.consent_reminders)}>
                    Consiento el tratamiento de mis datos personales (incluidos los de salud) por {clinicaDatos.legal_entity || clinicaDatos.clinic_name} para la prestación de los servicios médicos solicitados, así como el envío de recordatorios de cita por teléfono, SMS o WhatsApp.<Asterisk />
                  </Label>
                </div>
              </div>
              <FieldError msg={showErr('consent_reminders')} />

              <div className="flex items-start gap-3 p-3 rounded-md border border-border bg-white mt-2">
                <Checkbox
                  checked={form.consent_commercial}
                  onCheckedChange={(v) => setF('consent_commercial', !!v)}
                  className="mt-1"
                  data-testid="public-lopd-consent-commercial"
                />
                <div className="flex-1">
                  <Label className="text-sm leading-snug cursor-pointer text-muted-foreground" onClick={() => setF('consent_commercial', !form.consent_commercial)}>
                    Acepto recibir información comercial sobre tratamientos, novedades y promociones de la clínica. <span className="text-[11px] italic">(Opcional, puedes revocarlo en cualquier momento)</span>
                  </Label>
                </div>
              </div>
            </section>

            {/* CUESTIONARIO DE SALUD (documento único, 2026-07-26) */}
            <section id="salud-box">
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Cuestionario de salud</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Cuatro preguntas rápidas para que tu visita empiece con todo listo.
              </p>
              <div className="space-y-3">
                {[
                  { campo: 'alergias', texto: '¿Tienes alergia o intolerancia a algún medicamento?', detalle: '¿A qué medicamento(s)? *', ph: 'Ej.: penicilina, ibuprofeno…' },
                  { campo: 'medicacion', texto: '¿Tomas alguna medicación habitualmente?', detalle: '¿Qué medicación tomas? *', ph: 'Ej.: Sintrom, omeprazol 20 mg…' },
                  { campo: 'hialuronico', texto: textosAhPrevio(clinicaDatos.clinic_profile).pregunta, detalle: textosAhPrevio(clinicaDatos.clinic_profile).detalle, ph: textosAhPrevio(clinicaDatos.clinic_profile).ph },
                  { campo: 'cirugias', texto: '¿Has entrado alguna vez en quirófano?', detalle: '¿Qué cirugías? *', ph: 'Ej.: apendicitis en 2010, cesárea…' },
                ].map((b) => (
                  <div key={b.campo} className="border border-border rounded-lg p-3 space-y-2"
                    data-testid={`lopd-salud-${b.campo}`}>
                    <div className="text-sm font-medium text-[#13231E]">{b.texto}</div>
                    <div className="flex gap-2">
                      {[['Sí', true], ['No', false]].map(([lbl, val]) => (
                        <button key={lbl} type="button"
                          onClick={() => setS(b.campo, val)}
                          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                            salud[b.campo] === val
                              ? 'bg-[#2E6E5E] border-[#2E6E5E] text-white'
                              : 'bg-white border-border text-muted-foreground hover:text-[#13231E]'
                          }`}
                          data-testid={`lopd-salud-${b.campo}-${lbl.toLowerCase()}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                    {salud[b.campo] === true && (
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">{b.detalle}</div>
                        <textarea
                          rows={2}
                          maxLength={MAX.salud_text}
                          value={salud[`${b.campo}_text`]}
                          onChange={(e) => setS(`${b.campo}_text`, e.target.value)}
                          placeholder={b.ph}
                          className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6E5E]/40"
                          data-testid={`lopd-salud-${b.campo}-detalle`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* FOTOS + QUÉ MEJORAR — solo enlace de casa (la tablet no).
                Las fotos, además, solo a quien le tocan por su cita
                (`pide_fotos`); lo que le gustaría mejorar se pregunta
                siempre, porque es de la paciente y no de la cita. */}
            {esCasa && (
              <section id="fotos-box">
                {pideFotos && (<>
                <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Tus dos fotos</h2>
                <p className="text-xs text-muted-foreground mb-2">
                  Haznos llegar dos fotos como las de ejemplo: una de frente y otra
                  girada 45°. Buena luz, sin gafas y con el pelo retirado de la cara.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {FOTOS_CASA.map((f) => (
                    <div key={f.vista} className="space-y-1.5" data-testid={`lopd-foto-${f.vista}`}>
                      <div className="text-xs font-semibold text-[#13231E]">{f.titulo}</div>
                      <div className="relative rounded-md overflow-hidden border border-border bg-[#F2F2F0]">
                        <img src={fotos[f.vista]?.url || f.ejemplo} alt={f.titulo}
                          className="w-full aspect-[3/4] object-cover" />
                        {!fotos[f.vista]?.url && (
                          <span className="absolute top-1 left-1 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5">
                            EJEMPLO
                          </span>
                        )}
                        {fotos[f.vista]?.subiendo && (
                          <span className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-white animate-spin" />
                          </span>
                        )}
                        {fotos[f.vista]?.ok && (
                          <span className="absolute top-1 right-1 rounded-full bg-[#2E6E5E] p-1">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </span>
                        )}
                      </div>
                      <label className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium cursor-pointer transition-colors ${
                        fotos[f.vista]?.ok
                          ? 'border-[#2E6E5E] text-[#2E6E5E] bg-[rgba(46,110,94,0.06)]'
                          : 'border-[#2E6E5E] bg-[#2E6E5E] text-white hover:bg-[#1E4A3F]'
                      }`}>
                        <Camera className="w-4 h-4" />
                        {fotos[f.vista]?.ok ? 'Cambiar mi foto' : 'Subir mi foto'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => { subirFoto(f.vista, e.target.files?.[0]); e.target.value = ''; }}
                          data-testid={`lopd-foto-${f.vista}-input`} />
                      </label>
                      <p className="text-[10px] text-muted-foreground leading-tight">{f.ayuda}</p>
                    </div>
                  ))}
                </div>
                {fotoError && (
                  <p className="text-sm text-red-600 mb-3 flex items-start gap-1" data-testid="lopd-foto-error">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-[2px]" /> {fotoError}
                  </p>
                )}
                </>)}
                <div className="border border-border rounded-lg p-3 space-y-2" data-testid="lopd-mejorar">
                  <div className="text-sm font-medium text-[#13231E]">¿Qué te gustaría mejorar o tratar?</div>
                  <textarea rows={3} maxLength={MAX.mejorar} value={salud.mejorar || ''}
                    onChange={(e) => setS('mejorar', e.target.value)}
                    placeholder="Ej.: lo que te gustaría mejorar y desde cuándo te preocupa…"
                    className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6E5E]/40"
                    data-testid="lopd-mejorar-texto" />
                </div>
              </section>
            )}

            {/* FIRMA */}
            <section id="signature-box">
              <h2 className="text-[#13231E] font-display font-semibold text-lg border-b border-border pb-1 mb-3">Firma</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Firma con el dedo o un lápiz táctil dentro del recuadro. Tu firma constituye prueba del consentimiento.
              </p>
              <FirmaDedo
                ref={sigRef}
                testid="public-lopd-signature"
                alturaCls="h-48 sm:h-56"
                derecha={<span className="text-xs text-muted-foreground">{new Date().toLocaleDateString('es-ES')}</span>}
              />
            </section>

            {/* SUBMIT */}
            <div className="pt-2">
              {/* Por qué no se guardó el último envío: se queda hasta el
                  siguiente, con qué hacer y a quién llamar (23/9/2026). */}
              {aviso && (
                <div id="aviso-envio" role="alert"
                  className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm space-y-1"
                  data-testid="lopd-envio-rechazado">
                  <p className="font-semibold text-red-700 flex items-start gap-1">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-[2px]" /> {aviso.titulo}
                  </p>
                  {aviso.motivo && <p className="text-[#13231E]">{aviso.motivo}</p>}
                  <p className="text-[#13231E]">{aviso.ayuda}</p>
                </div>
              )}
              <Button
                onClick={submit}
                disabled={busy}
                className="w-full h-14 text-base bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="public-lopd-submit"
              >
                {busy ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando…</> : 'Firmar y enviar'}
              </Button>
              {submitAttempted && Object.keys(errors).length > 0 && (
                <p className="text-xs text-red-600 mt-2 text-center">Revisa los campos marcados con *</p>
              )}
            </div>
          </CardContent>
        </Card>
        <p className="text-center text-[11px] text-muted-foreground mt-4 mb-6">
          {pieClinica(clinicaDatos)}
        </p>
      </div>
    </div>
  );
}
